/**
 * Owned Media — Salud del portfolio (§6.1, AC-24, AC-33, AC-34): cuentas por
 * estado, fichas desactualizadas, accesos por vencer, requeridos/recomendados
 * sin completar, cuentas sin owner y capacity comprometido por persona. Vista
 * agregada para Dirección (§9.3) sin entrar al detalle.
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Clock, FileWarning, KeyRound, Users, UserX } from 'lucide-react';
import { StatCard } from '@/components/ui/stat-card';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EnumBadge } from '@/components/ui/enum-badge';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { trpc } from '@/lib/trpc';
import { useOmlCommunity } from '@/hooks/useOml';
import { FICHA_TONE, fichaLabel, fieldLabel, statusTone } from '@/components/owned-media/oml-ui';

export function OmlHealthPage() {
  const { t } = useTranslation();
  const health = trpc.oml.transversal.health.useQuery(undefined, { retry: false });
  const agg = trpc.oml.transversal.aggregates.useQuery(undefined, { retry: false });
  const { data: members = [] } = useOmlCommunity();
  const names = useMemo(() => Object.fromEntries(members.map((x) => [x.email, x.name])), [members]);
  if (health.isLoading) return <div className="flex justify-center py-20"><WinclapLoader size={48} className="text-foreground/25" /></div>;
  if (health.error || !health.data) return <p className="text-sm text-muted-foreground py-10 text-center">{health.error?.message}</p>;
  const h = health.data;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">{t('oml.nav.health', 'Salud del portfolio')}</h1>
        <p className="text-sm text-muted-foreground">{t('oml.health.subtitle', 'Qué fichas están desactualizadas, qué accesos vencen, qué falta cargar y cuánto capacity está comprometido. Datos al {{today}}.', { today: h.today })}</p>
      </div>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        <StatCard label={t('oml.health.accounts', 'Cuentas')} value={h.totals.accounts} tone="default" iconBox icon={Users} />
        <StatCard label={t('oml.health.stale', 'Fichas desactualizadas')} value={`${h.totals.stale} (${h.totals.stalePercent} %)`} tone={h.totals.stale ? 'bad' : 'good'} iconBox icon={Clock} />
        <StatCard label={t('oml.health.expiring', 'Accesos por vencer')} value={h.totals.expiringAccesses} tone={h.totals.expiringAccesses ? 'warn' : 'good'} iconBox icon={KeyRound} />
        <StatCard label={t('oml.health.risky', 'Accesos compartidos/personales')} value={h.totals.riskyAccesses} tone={h.totals.riskyAccesses ? 'bad' : 'good'} iconBox icon={AlertTriangle} />
        <StatCard label={t('oml.health.incomplete', 'Fichas incompletas')} value={h.totals.incomplete} tone={h.totals.incomplete ? 'warn' : 'good'} iconBox icon={FileWarning} />
        <StatCard label={t('oml.health.no_owner', 'Sin owner funcional')} value={h.totals.withoutOwner} tone={h.totals.withoutOwner ? 'bad' : 'good'} iconBox icon={UserX} />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">{t('oml.health.by_status', 'Cuentas por estado')}</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap gap-2">{Object.entries(h.totals.byStatus).map(([k, n]) => <EnumBadge key={k} value={k} tone={statusTone(k)} label={`${k}: ${n}`} />)}</CardContent>
        </Card>
        {agg.data && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">{t('oml.health.aggregates', 'Portfolio agregado')}</CardTitle></CardHeader>
            <CardContent className="grid gap-2 sm:grid-cols-2 text-sm">
              {([['byCountry', 'País'], ['byPlatform', 'Plataforma'], ['byGrowthPartner', 'Growth Partner'], ['byServiceType', 'Tipo de servicio']] as const).map(([k, label]) => (
                <div key={k}><div className="font-medium mb-1">{t(`oml.health.agg_${k}`, label)}</div><div className="flex flex-wrap gap-1">{agg.data[k].map((x) => <EnumBadge key={x.key} value={x.key} tone="neutral" label={`${x.key}: ${x.n}`} />)}</div></div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('oml.health.accounts_table', 'Cuentas')}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('oml.transversal.account', 'Cuenta')}</TableHead>
                <TableHead>{t('oml.filters.status', 'Estado de cuenta')}</TableHead>
                <TableHead>{t('oml.filters.ficha', 'Estado de la ficha')}</TableHead>
                <TableHead>{t('oml.health.days_since', 'Días sin edición')}</TableHead>
                <TableHead>{t('oml.completeness.title', 'Completitud de la ficha')}</TableHead>
                <TableHead>{t('oml.health.missing', 'Pendientes')}</TableHead>
                <TableHead>{t('oml.health.expiring', 'Accesos por vencer')}</TableHead>
                <TableHead>{fieldLabel(t, 'account', 'functionalOwner')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {h.accounts.map((a) => (
                <TableRow key={a.id}>
                  <TableCell><Link to={`/owned-media/accounts/${a.id}`} className="text-primary hover:underline font-medium">{a.name}</Link></TableCell>
                  <TableCell><EnumBadge value={a.status} tone={statusTone(a.status)} /></TableCell>
                  <TableCell><EnumBadge value={a.fichaStatus} tone={FICHA_TONE[a.fichaStatus]} label={fichaLabel(t, a.fichaStatus)} /></TableCell>
                  <TableCell>{a.daysSinceReview ?? '—'}</TableCell>
                  <TableCell>{a.completeness.percent} %{a.completeness.blocks.filter((b) => !b.ok).length > 0 && <span className="text-xs text-muted-foreground"> · {a.completeness.blocks.filter((b) => !b.ok).map((b) => t(`oml.completeness.${b.key}`, b.key)).join(', ')}</span>}</TableCell>
                  <TableCell className="text-xs">{[...a.completeness.missingRequired, ...a.completeness.missingRecommended].map((k) => fieldLabel(t, 'account', k)).join(' · ') || '—'}</TableCell>
                  <TableCell className="text-xs">{a.expiringAccesses.map((p) => `${p.platform} (${p.expiresAt})`).join(' · ') || '—'}{a.riskyAccesses > 0 && <span className="text-rose-600"> · {t('oml.health.risky_n', '{{n}} compartido/personal', { n: a.riskyAccesses })}</span>}</TableCell>
                  <TableCell>{a.functionalOwner ? names[String(a.functionalOwner)] ?? String(a.functionalOwner) : <EnumBadge value="sin owner" tone="danger" label={t('oml.health.no_owner', 'Sin owner funcional')} />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('oml.health.capacity', 'Capacity comprometido por persona')}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto p-0">
          <Table>
            <TableHeader><TableRow><TableHead>{t('oml.health.person', 'Persona')}</TableHead><TableHead>{t('oml.health.total', 'Total')}</TableHead><TableHead>{t('oml.health.detail', 'Detalle')}</TableHead></TableRow></TableHeader>
            <TableBody>
              {h.capacity.map((c) => (
                <TableRow key={c.email}>
                  <TableCell className="font-medium">{names[c.email] ?? c.name}</TableCell>
                  <TableCell><EnumBadge value={`${c.total} %`} tone={c.total > 100 ? 'danger' : c.total === 100 ? 'success' : 'neutral'} /></TableCell>
                  <TableCell className="text-xs">{c.accounts.map((x) => `${x.accountName} · ${x.role}${x.dedication !== null ? ` · ${x.dedication} %` : ` · ${t('oml.health.no_dedication', 'sin dedicación')}`}`).join(' | ')}</TableCell>
                </TableRow>
              ))}
              {h.capacity.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">{t('oml.detail.no_team', 'Sin asignaciones vigentes.')}</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
